import { useTranslation } from 'react-i18next';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

interface ContactChannel {
  id: 'demo' | 'sales' | 'support';
  title: string;
  body: string;
  value: string;
  action: string;
  href: string;
}

const DEMO_REQUEST_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Demo%20Request';
const SALES_CONTACT_HREF = 'mailto:sales@ablecomply.com';
const SUPPORT_CONTACT_HREF = 'mailto:support@ablecomply.com';

function resolveChannelHref(channelId: ContactChannel['id']): string {
  if (channelId === 'demo') {
    return DEMO_REQUEST_HREF;
  }

  if (channelId === 'support') {
    return SUPPORT_CONTACT_HREF;
  }

  return SALES_CONTACT_HREF;
}

export default function ContactPage() {
  const { t } = useTranslation('marketing');
  const channels = (
    t('pages.contact.channels', { returnObjects: true }) as Array<Omit<ContactChannel, 'href'>>
  ).map((channel) => ({
    ...channel,
    href: resolveChannelHref(channel.id),
  }));
  const guidancePoints = t('pages.contact.guidance.points', { returnObjects: true }) as string[];

  return (
    <PublicContentPage
      metaTitle={t('pages.contact.meta.title')}
      metaDescription={t('pages.contact.meta.description')}
      canonicalPath="/contact"
      eyebrow={t('pages.contact.hero.eyebrow')}
      title={t('pages.contact.hero.title')}
      intro={t('pages.contact.hero.intro')}
      schemaType="ContactPage"
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.contact.sidebar.title')}</h2>
            <p>{t('pages.contact.sidebar.body')}</p>
            <div className="acc-public-page-actions">
              <PublicCtaLink href="/login" variant="ghost">
                {t('actions.signIn')}
              </PublicCtaLink>
              <PublicCtaLink href="/register">
                {t('footer.support.register')}
              </PublicCtaLink>
            </div>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.contact.sidebar.responseTitle')}</h2>
            <p>{t('pages.contact.sidebar.responseBody')}</p>
          </section>
        </>
      )}
    >
      <section className="acc-public-page-card">
        <h2>{t('pages.contact.channelsHeading')}</h2>
        <p>{t('pages.contact.channelsIntro')}</p>
        <div className="acc-public-page-grid">
          {channels.map((channel) => (
            <article key={channel.title} className="acc-public-page-subcard">
              <h3>{channel.title}</h3>
              <p>{channel.body}</p>
              <div className="acc-public-page-subcard-value">{channel.value}</div>
              <div className="acc-public-page-actions">
                <PublicCtaLink href={channel.href}>{channel.action}</PublicCtaLink>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="acc-public-page-card">
        <h2>{t('pages.contact.guidance.title')}</h2>
        <p>{t('pages.contact.guidance.body')}</p>
        <ul className="acc-public-page-list">
          {guidancePoints.map((point) => (
            <li key={point}>{point}</li>
          ))}
        </ul>
      </section>
    </PublicContentPage>
  );
}
